import { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Lightbox({ items, active, onClose, onPrev, onNext, testidPrefix = "lightbox" }) {
  const close = useCallback(() => onClose(), [onClose]);

  useEffect(() => {
    if (active === null) return;
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.__lenis?.start();
      window.removeEventListener("keydown", onKey);
    };
  }, [active === null, onClose, onPrev, onNext]);

  return (
    <AnimatePresence>
      {active !== null && (
        <motion.div
          data-testid={`${testidPrefix}-overlay`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={close}
          className="fixed inset-0 z-[70] bg-[#1C1B1A]/95 backdrop-blur-sm flex flex-col items-center justify-center p-6"
        >
          <button
            data-testid={`${testidPrefix}-close-button`}
            onClick={close}
            aria-label="Chiudi"
            className="absolute top-6 right-6 w-12 h-12 rounded-full border border-white/30 flex items-center justify-center text-white hover:bg-[#E30613] hover:border-[#E30613] transition-colors duration-300"
          >
            <X className="w-5 h-5" />
          </button>
          <button
            data-testid={`${testidPrefix}-prev-button`}
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            aria-label="Immagine precedente"
            className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-white/30 flex items-center justify-center text-white hover:bg-[#E30613] hover:border-[#E30613] transition-colors duration-300"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            data-testid={`${testidPrefix}-next-button`}
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            aria-label="Immagine successiva"
            className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-white/30 flex items-center justify-center text-white hover:bg-[#E30613] hover:border-[#E30613] transition-colors duration-300"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <motion.img
            key={items[active].img}
            data-testid={`${testidPrefix}-image`}
            src={items[active].img}
            alt={items[active].title || items[active].alt || ""}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[80vh] max-w-full object-contain shadow-2xl"
          />
          <p data-testid={`${testidPrefix}-caption`} onClick={(e) => e.stopPropagation()} className="mt-6 text-white/90 text-sm font-semibold tracking-wide">
            {items[active].title}
            <span className="ml-3 text-white/50 text-xs">{active + 1} / {items.length}</span>
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
